import { useEffect, useState } from "react";

import AppButton from "../common/AppButton";
import AppForm from "../common/AppForm";
import AppLoader from "../common/AppLoader";
import AppModal from "../common/AppModal";
import AppTable from "../common/AppTable";
import CSVModal from "../common/CsvFormat";

import { useAsync, usePagination } from "../../hooks";
import {
  getProduct,
  postFile,
  downloadFile,
  downloadDailyChanges,
  postProduct,
  deleteProduct,
  editProduct,
} from "../../services";
import { getCompetitors, Competitor } from "../../services/getCompetitors";
import { CreateProductData, GetProduct, ProductData } from "../../interface";
import { useUser } from "../../context/UserContext";
import { productForm } from "../../constants";
import { productSchema } from "../../utils";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import { updateWatchList } from "../../services/patchWatchList";
import { Heart, HeartHandshake } from "lucide-react";
import { EditProductForm } from "../EditProductForm/EditProductForm";

const IconWrapper = styled("span")(() => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  width: 28,
  height: 28,
  margin: "0 6px",
  "& .default-icon": { display: "block" },
  "& .hover-icon": {
    display: "none",
    position: "absolute",
    top: 0,
    left: 0,
  },
  "&:hover .default-icon": { display: "none" },
  "&:hover .hover-icon": { display: "block" },
}));

interface ProductsTableProps {
  onProductClick: (productId: number, productName: string) => void;
}

const ProductsTable = ({ onProductClick }: ProductsTableProps) => {
  const [products, setProducts] = useState<ProductData[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState("");
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductData | null>(
    null,
  );
  const [deleteProductId, setDeleteProductId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [filterType, setFilterType] = useState<
    "all" | "not_cheapest" | "price_changes" | "watchlisted"
  >("all");
  const [selectedCompetitors, setSelectedCompetitors] = useState<string[]>([]);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);

  const [modalMode, setModalMode] = useState<"add" | "edit" | "delete" | null>(
    null,
  );

  const { user } = useUser();
  const { loading, error, execute } = useAsync<
    GetProduct,
    [number, number, string, boolean, boolean, boolean, string, string, string[]]
  >();
  const {
    loading: loadingPost,
    execute: executePost,
    error: errorPost,
  } = useAsync<string, [CreateProductData]>();
  const {
    loading: loadingDelete,
    execute: executeDelete,
    error: errorDelete,
  } = useAsync<string, [number]>();
  const {
    loading: updateLoading,
    error: updateError,
    execute: updateExecute,
  } = useAsync<string, [number, Partial<ProductData>]>();
  const { meta, setMeta, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5, "productsTable");

  useEffect(() => {
    getCompetitors().then(setCompetitors).catch(console.error);
  }, []);

  useEffect(() => {
    execute(
      getProduct,
      meta.currentPage,
      meta.pageSize,
      search,
      filterType === "not_cheapest",
      filterType === "price_changes",
      filterType === "watchlisted",
      sortBy,
      sortOrder,
      selectedCompetitors,
    ).then((response) => {
      setProducts(response.data);
      setMeta(response.meta);
    });
  }, [
    meta.currentPage,
    meta.pageSize,
    search,
    filterType,
    sortBy,
    sortOrder,
    selectedCompetitors,
    execute,
    setMeta,
  ]);

  const handleOpenAddModal = () => {
    setModalMode("add");
    setSelectedProduct(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setModalMode(null);
    setSelectedProduct(null);
    setDeleteProductId(null);
    setOpenModal(false);
  };

  const handleOpenEditModal = (product: ProductData) => {
    setModalMode("edit");
    setSelectedProduct(product);
    setOpenModal(true);
  };

  const handleOpenDeleteModal = (id: number) => {
    setModalMode("delete");
    setDeleteProductId(id);
    setOpenModal(true);
  };

  const handleSubmit = async (values: {
    name: string;
    sku: string;
    clientPrice: number;
    costPrice: number;
    urls: string[];
    ourSiteUrl: string;
  }) => {
    try {
      if (modalMode === "edit" && selectedProduct) {
        return;
      } else if (modalMode === "add") {
        handleCloseModal();

        const message = await executePost(postProduct, {
          ...values,
          urls: values.urls.filter((u) => u.trim() !== ""),
        });

        console.log("✅ Product added:", message);

        const response = await execute(
          getProduct,
          meta.currentPage,
          meta.pageSize,
          search,
          filterType === "not_cheapest",
          filterType === "price_changes",
          filterType === "watchlisted",
          sortBy,
          sortOrder,
          selectedCompetitors,
        );

        setProducts(response.data);
        setMeta(response.meta);
      }
    } catch (err) {
      console.error("❌ Failed to save product", err);
    }
  };

  const handleToggleWatchlist = async (product: ProductData) => {
    try {
      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id ? { ...p, isWatchlisted: !p.isWatchlisted } : p,
        ),
      );
      await updateWatchList(product.id, {
        isWatchlisted: !product.isWatchlisted,
      });
    } catch (err) {
      console.error("Failed to toggle watchlist", err);
    }
  };

  const handleDelete = async () => {
    if (!deleteProductId) return;

    handleCloseModal();

    try {
      const message = await executeDelete(deleteProduct, deleteProductId);
      console.log("✅ Deleted:", message);
      setProducts((prev) =>
        prev.filter((product) => product.id !== deleteProductId),
      );
      setMeta((prev) => {
        const newTotalRecords = prev.totalRecords - 1;
        const newTotalPages = Math.ceil(newTotalRecords / prev.pageSize);
        const newCurrentPage = Math.min(prev.currentPage, newTotalPages || 1);
        return {
          ...prev,
          totalRecords: newTotalRecords,
          totalPages: newTotalPages,
          currentPage: newCurrentPage,
        };
      });
      setDeleteProductId(null);
    } catch (err) {
      console.error("❌ Delete error:", err);
    }
  };

  const handleImportCSV = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoadingOverlay(true);
      await postFile(formData);
      execute(
        getProduct,
        meta.currentPage,
        meta.pageSize,
        search,
        filterType === "not_cheapest",
        filterType === "price_changes",
        filterType === "watchlisted",
        sortBy,
        sortOrder,
        selectedCompetitors,
      ).then((response) => {
        setProducts(response.data);
        setMeta(response.meta);
      });
    } catch (err: any) {
      console.error(err);
      alert(`${err.message}`);
    } finally {
      setLoadingOverlay(false);
    }
  };

  const handleSort = (columnId: string) => {
    const newSortOrder =
      sortBy === columnId && sortOrder === "asc" ? "desc" : "asc";

    setSortBy(columnId);
    setSortOrder(newSortOrder);
    setMeta({ ...meta, currentPage: 1 });
  };

  const competitorNames: string[] =
    selectedCompetitors.length === 0
      ? competitors.map((c) => c.name)
      : selectedCompetitors;

  const extendedColumns = [
    {
      id: "name",
      label: "Name",
      sortable: true,
      wrapText: true,
      cellTitle: (product: ProductData) => product.name,
      render: (product: ProductData) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {product.name}
        </span>
      ),
    },
    {
      id: "sku",
      label: "SKU",
      sortable: true,

      render: (product: ProductData) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {product.sku}
        </span>
      ),
    },
    {
      id: "clientPrice",
      label: "Derma Direct",
      sortable: true,

      render: (product: ProductData) => {
        const clientPriceNum = parseFloat(
          parseFloat(String(product.clientPrice ?? 0)).toFixed(2),
        );
        const byWebsite = (product.variants ?? []).reduce<
          Record<string, NonNullable<ProductData["variants"]>>
        >((acc, v) => {
          if (!v.websiteName) return acc;
          if (!acc[v.websiteName]) acc[v.websiteName] = [];
          acc[v.websiteName].push(v);
          return acc;
        }, {});
        const latestCompetitorPrices = Object.values(byWebsite)
          .map(
            (variants) =>
              [...variants].sort((a, b) => {
                const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return bTime - aTime;
              })[0],
          )
          .filter(
            (v) =>
              v?.effectivePrice != null &&
              v.effectivePrice > 0 &&
              !isNaN(parseFloat(String(v.effectivePrice))),
          )
          .map((v) =>
            parseFloat(parseFloat(String(v!.effectivePrice)).toFixed(2)),
          );

        const cheapestCompetitorPrice =
          latestCompetitorPrices.length > 0
            ? Math.min(...latestCompetitorPrices)
            : null;

        let bgColor = "";
        let textColor = "";

        if (cheapestCompetitorPrice !== null) {
          if (clientPriceNum > cheapestCompetitorPrice) {
            bgColor = "#f8d7da";
            textColor = "#842029";
          } else if (cheapestCompetitorPrice - clientPriceNum >= 3) {
            bgColor = "#fff3cd";
            textColor = "#664d03";
          } else {
            bgColor = "#d1e7dd";
            textColor = "#0f5132";
          }
        }

        return (
          <span
            style={{
              padding: "2px 6px",
              borderRadius: "4px",
              backgroundColor: bgColor,
              color: textColor,
            }}
          >
            {product.clientPrice
              ? parseFloat(String(product.clientPrice)).toFixed(2)
              : "N/A"}
          </span>
        );
      },
    },
    {
      id: "costPrice",
      label: "Cost Price",
      sortable: true,

      render: (product: ProductData) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {product.costPrice || "N/A"}
        </span>
      ),
    },

    {
      id: "markupPercentage",
      label: "Margin Percentage",
      sortable: true,

      render: (product: ProductData) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {product.markupPercentage ? `${product.markupPercentage} %` : "N/A"}
        </span>
      ),
    },
    ...competitorNames.map((websiteName, index) => ({
      id: `competitor_${index + 1}`,
      label: websiteName,

      render: (product: ProductData) => {
        const matchingVariants =
          product.variants?.filter((v) => v.websiteName === websiteName) ?? [];

        if (matchingVariants.length === 0) return "N/A";

        // Use variant with latest updatedAt for price/link (null/undefined treated as oldest)
        const latestVariant = [...matchingVariants].sort((a, b) => {
          const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return bTime - aTime;
        })[0];

        // For color indication: only compare against latest-updated variant per competitor
        const byWebsite = (product.variants ?? []).reduce<
          Record<string, typeof product.variants>
        >((acc, v) => {
          if (!v.websiteName) return acc;
          if (!acc[v.websiteName]) acc[v.websiteName] = [];
          acc[v.websiteName].push(v);
          return acc;
        }, {});
        const latestPricesOnly = Object.values(byWebsite)
          .map(
            (variants) =>
              [...variants].sort((a, b) => {
                const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return bTime - aTime;
              })[0],
          )
          .filter(
            (v) =>
              v?.effectivePrice != null &&
              v.effectivePrice > 0 &&
              !isNaN(parseFloat(String(v.effectivePrice))),
          )
          .map((v) =>
            parseFloat(parseFloat(String(v!.effectivePrice)).toFixed(2)),
          );

        const clientPriceNum = parseFloat(
          parseFloat(String(product.clientPrice ?? 0)).toFixed(2),
        );

        const cheapestPrice =
          latestPricesOnly.length > 0
            ? Math.min(...latestPricesOnly, clientPriceNum)
            : null;

        const currentPrice = parseFloat(
          parseFloat(String(latestVariant.effectivePrice)).toFixed(2),
        );

        const isInStock = latestVariant.stockStatus
          ?.toLowerCase()
          .includes("in stock");
        const stockColor = isInStock ? "#22c55e" : "#ef4444";
        const stockStatusText = isInStock ? "In Stock" : "Out of Stock";

        const isCheapest =
          cheapestPrice !== null &&
          currentPrice === cheapestPrice &&
          currentPrice !== clientPriceNum;
        const priceStyle = isCheapest
          ? {
              padding: "2px 6px",
              borderRadius: "4px",
              backgroundColor: "#d1e7dd",
              color: "#0f5132",
            }
          : {};

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <a
              href={latestVariant.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
                color: "black",
                fontSize: "16px",
                ...priceStyle,
              }}
            >
              {latestVariant.effectivePrice?.toFixed(2) ?? "N/A"}
            </a>

            {/* Single stock status indicator */}
            <div
              className="lf-stock-indicator"
              style={{
                position: "relative",
                display: "inline-block",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  display: "inline-block",
                  backgroundColor: stockColor,
                }}
              />

              <div
                className="lf-stock-tooltip"
                style={{ pointerEvents: "none" }}
              >
                <p style={{ margin: 0 }}>{stockStatusText}</p>
              </div>
            </div>

            <style>
              {`
            /* Stock indicator tooltip styling */
            .lf-stock-indicator .lf-stock-tooltip {
              visibility: hidden;
              opacity: 0;
              background-color: #000;
              color: #fff;
              border-radius: 4px;
              padding: 6px 8px;
              position: absolute;
              bottom: 125%;
              left: 50%;
              transform: translateX(-50%);
              white-space: nowrap;
              font-size: 12px;
              line-height: 1;
              transition: opacity 0.15s ease-in-out;
              z-index: 9999;
              box-shadow: 0 2px 6px rgba(0,0,0,0.2);
              pointer-events: none;
            }

            /* Show tooltip on hover */
            .lf-stock-indicator:hover .lf-stock-tooltip {
              visibility: visible;
              opacity: 1;
            }
          `}
            </style>
          </div>
        );
      },
    })),

    {
      id: "actions",
      label: "Actions",
      render: (product: ProductData) => (
        <div className="d-flex align-items-center gap-3">
          <IconWrapper>
            <button
              className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 rounded-pill transition-all`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleWatchlist(product);
              }}
              style={{
                transition: "all 0.2s ease-in-out",
                color: "#000",
              }}
            >
              {product.isWatchlisted ? (
                <div
                  style={{
                    background: "#4fd1c5",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "1000px",
                  }}
                >
                  <HeartHandshake size={16} color="#fff" />
                </div>
              ) : (
                <div
                  style={{
                    background: "#F8F9FA",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "1000px",
                  }}
                >
                  <Heart size={16} color="#000" />
                </div>
              )}
            </button>
          </IconWrapper>
          <IconWrapper>
            <EditOutlinedIcon
              className="default-icon"
              sx={{ color: "#1976d2", cursor: "pointer" }}
            />
            <EditIcon
              className="hover-icon"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditModal(product);
              }}
              sx={{ color: "#1976d2", cursor: "pointer" }}
            />
          </IconWrapper>
          <IconWrapper>
            <DeleteOutlinedIcon
              className="default-icon"
              sx={{ color: "#d32f2f", cursor: "pointer" }}
            />
            <DeleteIcon
              className="hover-icon"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteModal(product.id!);
              }}
              sx={{ color: "#d32f2f", cursor: "pointer" }}
            />
          </IconWrapper>
        </div>
      ),
    },
  ];

  const isLoading =
    loadingOverlay || loadingPost || loadingDelete || updateLoading;

  return (
    <>
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <AppLoader />
        </div>
      )}

      {modalMode === "add" ? (
        <AppModal
          title={"Add Product"}
          open={openModal}
          handleClose={handleCloseModal}
          action={"Add Product"}
        >
          <AppForm
            inputFields={productForm}
            initialValues={{
              name: "",
              sku: "",
              clientPrice: "",
              costPrice: "",
              ourSiteUrl: "",
              urls: [""],
            }}
            schema={productSchema}
            handleSubmit={handleSubmit}
          />
        </AppModal>
      ) : (
        <EditProductForm
          onClose={handleCloseModal}
          isOpen={modalMode === "edit"}
          initialValue={selectedProduct}
          OnEditProducts={(values) => {
            setProducts((prev) =>
              prev.map((product) =>
                product.id === selectedProduct?.id
                  ? { ...product, ...values }
                  : product,
              ),
            );
          }}
        />
      )}

      {modalMode === "delete" && (
        <AppModal
          title="Delete Product"
          open={openModal}
          handleClose={handleCloseModal}
          onSubmit={handleDelete}
          action="Delete"
          color="#dc3545"
        >
          <p>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </p>
        </AppModal>
      )}

      <CSVModal
        open={csvModalOpen}
        handleClose={() => setCsvModalOpen(false)}
        handleConfirm={(file) => {
          setCsvModalOpen(false);
          if (file) {
            handleImportCSV(file);
          }
        }}
        title="📄 CSV Upload Format for Products"
        formatDetails={
          <>
            <li>
              <b>name</b>: Product name
            </li>
            <li>
              <b>clientPrice</b>: Price of client (number)
            </li>
            <li>
              <b>sku</b>: Client's SKU
            </li>
            <li>
              <b>costPrice</b>: Cost Price (number)
            </li>
            <li>
              <b>ourSiteUrl</b>: Your product URL
            </li>
            <li>
              <b>url1</b>, <b>url2</b> ... : Optional variant links
            </li>
          </>
        }
        sampleFileUrl="/example_data.csv"
        sampleFileName="example_data.csv"
      />

      <div className="d-flex justify-content-between align-items-center">
        <AppButton
          label="Export Daily Changes"
          size="sm"
          fullWidth={false}
          disabled={loadingOverlay}
          onClick={downloadDailyChanges}
        />

        <div className="mb-3 d-flex justify-content-end gap-2">
          <AppButton
            label="Export CSV"
            size="sm"
            fullWidth={false}
            disabled={loadingOverlay}
            onClick={downloadFile}
          />
          {user?.user_role !== "viewer" && (
            <>
              <AppButton
                label="Import CSV"
                size="sm"
                fullWidth={false}
                disabled={loadingOverlay}
                onClick={() => setCsvModalOpen(true)}
              />
              <AppButton
                label="Add Product"
                size="sm"
                fullWidth={false}
                onClick={handleOpenAddModal}
              />
            </>
          )}
        </div>
      </div>
      <AppTable<ProductData>
        title="Products Table"
        columns={extendedColumns}
        renderRowKey={(product) => product.id}
        rows={products}
        competitors={competitors}
        selectedCompetitors={selectedCompetitors}
        onCompetitorChange={(values) => {
          setSelectedCompetitors(values);
          setMeta({ ...meta, currentPage: 1 });
        }}
        loading={loading}
        error={error}
        count={meta.totalRecords}
        page={meta.currentPage - 1}
        onPageChange={handleChangePage}
        rowsPerPage={meta.pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onRowClick={(product) => onProductClick(product.id, product.name)}
        isRowClickable
        searchValue={search}
        onSearchChange={(value) => {
          setMeta({ ...meta, currentPage: 1 });
          setSearch(value);
        }}
        searchPlaceholder="Search…"
        filterType={filterType}
        onFilterChange={(value) => {
          setMeta({ ...meta, currentPage: 1 });
          setFilterType(value);
        }}
        onSort={(columnId) => handleSort(columnId)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        lastRunDate={
          meta.lastRunDate != null && meta.lastRunDate !== ""
            ? new Date(meta.lastRunDate).toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              })
            : products.length > 0
              ? (() => {
                  const latest = products.reduce<number | null>(
                    (best, product) => {
                      const t = product.lastPriceUpdatedAt
                        ? new Date(product.lastPriceUpdatedAt).getTime()
                        : 0;
                      return isNaN(t)
                        ? best
                        : best == null || t > best
                          ? t
                          : best;
                    },
                    null,
                  );
                  return latest != null
                    ? new Date(latest).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : null;
                })()
              : null
        }
      />
    </>
  );
};

export default ProductsTable;
